import {
  useProjectId,
  useRegenerateTranscriptWithAudioMutation,
  useUploadMeetingTranscriptMutation,
} from '@consigli/hooks';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { getBackendErrorCode, getNumericStatus } from '../utils/mutation-error';
import { isTextOnlyTranscripts } from '../utils/transcript-quality';
import {
  getUploadErrorDetails,
  getUploadErrorKey,
  getUploadErrorKeyForCode,
  validateTranscriptFile,
  type UploadErrorDetails,
} from '../utils/transcript-upload';

import type { BuildParamsResult, FetchTranscriptsOptions } from './use-meeting-summary-data';
import type { Meeting } from '../utils/export-types';
import type { Transcript } from '@consigli/types';

const T_PREFIX = 'meeting-secretary.meetings.';

interface UseTextOnlyTranscriptParams {
  meeting: Pick<Meeting, 'id' | 'joinUrl'>;
  buildParams: (msToken?: string) => Promise<BuildParamsResult>;
  fetchTranscripts: (options?: FetchTranscriptsOptions) => Promise<void>;
  applyTranscripts: (fetched: Transcript[], audioUnavailable?: boolean) => void;
  resetGeneratedData: () => void;
}

/**
 * The two ways out of a text-only transcript: anyone uploads the Teams file when the meeting
 * cannot be read from Teams, and the organizer replaces the text with the audio run.
 */
export const useTextOnlyTranscript = ({
  meeting,
  buildParams,
  fetchTranscripts,
  applyTranscripts,
  resetGeneratedData,
}: UseTextOnlyTranscriptParams) => {
  const { t } = useTranslation();
  const projectId = useProjectId();
  const [upload] = useUploadMeetingTranscriptMutation();
  const [regenerate] = useRegenerateTranscriptWithAudioMutation();
  const [isUploading, setIsUploading] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  // An i18n key, so the component decides how to show it.
  const [uploadError, setUploadError] = useState<string | null>(null);
  // The meeting a refused file belongs to, for the message that names it.
  const [uploadErrorDetails, setUploadErrorDetails] = useState<UploadErrorDetails>({});

  const clearUploadError = useCallback(() => {
    setUploadError(null);
    setUploadErrorDetails({});
  }, []);

  const uploadTranscript = useCallback(
    async (file: File): Promise<boolean> => {
      clearUploadError();

      const fileError = validateTranscriptFile(file);
      if (fileError) {
        setUploadError(getUploadErrorKeyForCode(fileError));
        return false;
      }

      setIsUploading(true);
      try {
        const response = await upload({ meetingId: meeting.id, projectId, file }).unwrap();
        const wrapper = response.transcripts as {
          transcripts?: Transcript[];
          audioUnavailable?: boolean;
        };
        applyTranscripts(wrapper.transcripts ?? [], wrapper.audioUnavailable === true);
        toast.success(t(`${T_PREFIX}transcript-upload-success`));
        return true;
      } catch (err) {
        const code = getBackendErrorCode(err);
        if (code === 'already_has_transcript') {
          // Someone got there first; show what the meeting now has.
          toast.info(t(`${T_PREFIX}transcript-upload-already-has`));
          await fetchTranscripts();
        } else {
          console.error('Error uploading transcript:', err);
          setUploadError(getUploadErrorKey(err));
          setUploadErrorDetails(getUploadErrorDetails(err));
        }
        return false;
      } finally {
        setIsUploading(false);
      }
    },
    [upload, meeting.id, projectId, applyTranscripts, fetchTranscripts, clearUploadError, t],
  );

  // Fetches the transcripts again and, if audio segments came back, drops what was generated
  // from the text. Returns what was fetched, or null when that did not happen.
  const reloadTranscripts = useCallback(async (): Promise<Transcript[] | null> => {
    const outcome: { fetched: Transcript[] | null } = { fetched: null };
    await fetchTranscripts({
      beforeCommit: (fetched) => {
        outcome.fetched = fetched;
        if (!isTextOnlyTranscripts(fetched)) resetGeneratedData();
      },
    });
    return outcome.fetched;
  }, [fetchTranscripts, resetGeneratedData]);

  const { joinUrl } = meeting;
  const regenerateWithAudio = useCallback(async (): Promise<boolean> => {
    if (!joinUrl) {
      toast.error(t(`${T_PREFIX}transcript-regenerate-error`));
      return false;
    }

    setIsRegenerating(true);
    try {
      const params = await buildParams();
      await regenerate({ ...params, joinUrl }).unwrap();

      // The run succeeds without changing anything when no recording could be used, so the
      // outcome is read from the segments.
      const fetched = await reloadTranscripts();
      if (fetched === null) {
        toast.error(t(`${T_PREFIX}transcript-regenerate-error`));
        return false;
      }
      if (isTextOnlyTranscripts(fetched)) {
        toast.warn(t(`${T_PREFIX}transcript-regenerate-no-recording`));
        return false;
      }
      toast.success(t(`${T_PREFIX}transcript-regenerate-success`));
      return true;
    } catch (err) {
      const code = getBackendErrorCode(err);
      const status = getNumericStatus(err);
      if (code === 'organizer_only') {
        toast.error(t(`${T_PREFIX}transcript-regenerate-organizer-only`));
      } else if (code === 'uploaded_by_organizer') {
        // No recording backs an organizer's own upload, so there is nothing to re-run.
        toast.info(t(`${T_PREFIX}transcript-regenerate-uploaded-by-organizer`));
        await reloadTranscripts();
      } else if (code === 'audio_unavailable') {
        // A run already found no audio. The banner says so once the transcripts are read again.
        await reloadTranscripts();
      } else if (code === 'already_regenerated') {
        // Done elsewhere; show the result and let the button go away.
        toast.info(t(`${T_PREFIX}transcript-regenerate-already-done`));
        await reloadTranscripts();
      } else if (code === 'busy') {
        toast.warn(t(`${T_PREFIX}transcript-regenerate-busy`));
      } else if (status === 401 || status === 404) {
        toast.error(t(`${T_PREFIX}transcript-regenerate-unreachable`));
      } else {
        console.error('Error regenerating transcript with audio:', err);
        toast.error(t(`${T_PREFIX}transcript-regenerate-error`));
      }
      return false;
    } finally {
      setIsRegenerating(false);
    }
  }, [buildParams, regenerate, joinUrl, reloadTranscripts, t]);

  return {
    uploadTranscript,
    isUploading,
    uploadError,
    uploadErrorDetails,
    clearUploadError,
    regenerateWithAudio,
    isRegenerating,
  };
};
